import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { PersonalInfo as PersonalInfoType } from "../types";
import { Close as CloseIcon, FormatAlignJustify } from "@mui/icons-material";
import { myAccountService } from "../services/myAccountService";
import { Toast } from "../../../components/common/Toast/Toast";
import editIcon from "src/assets/icons/edit.svg";
import { COUNTRY_CODES } from "../../auth/constants/countryCodes";
import { alignProperty } from "@mui/material/styles/cssUtils";

type PersonalProps = {
  children?: React.ReactNode;
};

const selectInAdornmentSx = {
  minWidth: 78,
  "& .MuiSelect-select": {
    padding: "0 10px 0 12px",
    display: "flex",
    alignItems: "center",
    height: 48,
    fontSize: 14,
  },
};

export const PersonalInfo: React.FC<PersonalProps> = () => {
  const [personalInfo, setPersonalInfo] = useState<PersonalInfoType | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [editing, setEditing] = useState(false);

  const showToast = (message: string, severity: "success" | "error") =>
    setToast({ open: true, message, severity });

  const [savingState, setSavingState] = useState<boolean>(false);
  const [data, setData] = useState<PersonalInfoType>({
    firstName: "",
    lastName: "",
    phoneCode: "+66",
    phoneNumber: "",
    email: "",
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      height: 48,
      borderRadius: "12px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#111" },
      "&:hover fieldset": { borderColor: "#111" },
      "&.Mui-focused fieldset": { borderColor: "#111", borderWidth: 1 },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      fontSize: 14,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
  };

  const fetchPersonalInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await myAccountService.getPersonalInfo();
      setPersonalInfo(data);
    } catch (err: any) {
      console.error("Error fetching personal info:", err);
      setError(err?.message || "Failed to load personal information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonalInfo();
  }, []);

  useEffect(() => {
    if (personalInfo) {
      console.log("PersonalInfo data:", personalInfo);
      setData({
        firstName: personalInfo.firstName || "",
        lastName: personalInfo.lastName || "",
        phoneCode: personalInfo?.phoneCode || "+66",
        phoneNumber:
          personalInfo.phoneNumber || personalInfo.billing?.phone || "",
        email: personalInfo.email || "",
      });
    }
  }, [personalInfo]);

  const handleSave = async () => {
    try {
      setSavingState(true);
      setError(null);
      const updatedData = await myAccountService.updatePersonalInfo(data);
      //setPersonalInfo(updatedData);
      if (updatedData != null) {
        console.log("save success " + updatedData);
      }
      setEditing(false);
      showToast("Personal Information updated", "success");
      fetchPersonalInfo();
    } catch (err: any) {
      showToast("Failed to save personal information", "error");
    } finally {
      setSavingState(false);
    }
  };

  const handleChange =
    (key: keyof PersonalInfoType) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData({ ...data, [key]: e.target.value });
    };

  const handleCountryCodeChange = (e: any) => {
    console.log("Country code changed to:", e.target.value);
    setData({ ...data, phoneCode: e.target.value });
  };

  const handleCancel = () => {
    if (personalInfo) {
      setData({
        firstName: personalInfo.firstName || "",
        lastName: personalInfo.lastName || "",
        phoneCode: personalInfo.phoneCode || "+66",
        phoneNumber:
          personalInfo.phoneNumber || personalInfo.billing?.phone || "",
        email: personalInfo.email || "",
      });
    }
    setEditing(false);
  };

  return (
    <Box sx={{ mx: 'auto', mt: 4, mr: { xs: 0, md: 50 } }}>
      <Typography
        variant="h3"
        align="center"
        sx={{ mb: 6, fontFamily: "Nandia" }}
      >
        My Account
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div>
          <Typography
            sx={{
              fontFamily: "Nandia",
              fontSize: 18,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Personal Information
          </Typography>
        </div>
        <div>
          {!editing ? (
            <IconButton onClick={() => setEditing(true)} aria-label="edit">
              <img
                src={editIcon}
                alt="Edit"
                style={{ width: 24, marginRight: 8 }}
              />
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: 14,
                  fontWeight: 100,
                  color: "text.primary",
                }}
              >
                Edit
              </Typography>
            </IconButton>
          ) : (
            <IconButton onClick={handleCancel} sx={{ padding: 0 }}>
              <CloseIcon />
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: 14,
                  fontWeight: 100,
                  color: "text.primary",
                }}
              >
                Discard
              </Typography>
            </IconButton>
          )}
        </div>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Personal Info Form */}
      {!loading && (
        <Grid container spacing={2}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Grid sx={{ flex: "1 1 300px", maxWidth: { md: "50%" } }}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: 14,
                    fontWeight: 100,
                    mb: 1,
                  }}
                >
                  First name
                </Typography>
                <TextField
                  fullWidth
                  value={data.firstName}
                  onChange={handleChange("firstName")}
                  InputProps={{ readOnly: !editing }}
                  sx={
                    editing
                      ? fieldSx
                      : {
                          "& fieldset": {
                            border: "none",
                          },
                          backgroundColor: "#f7f7f7",
                          borderRadius: "10px",
                          "& input": {
                            padding: "12px 14px",
                          },
                        }
                  }
                />
              </FormControl>
            </Grid>
            <Grid sx={{ flex: "1 1 300px", maxWidth: { md: "50%" } }}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: 14,
                    fontWeight: 100,
                    mb: 1,
                  }}
                >
                  Last name
                </Typography>
                <TextField
                  fullWidth
                  value={data.lastName}
                  onChange={handleChange("lastName")}
                  InputProps={{ readOnly: !editing }}
                  sx={
                    editing
                      ? fieldSx
                      : {
                          "& fieldset": {
                            border: "none",
                          },
                          backgroundColor: "#f7f7f7",
                          borderRadius: "10px",
                          "& input": {
                            padding: "12px 14px",
                          },
                        }
                  }
                />
              </FormControl>
            </Grid>
            <Grid sx={{ flex: "1 1 300px", maxWidth: { md: "50%" } }}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: 14,
                    fontWeight: 100,
                    mb: 1,
                  }}
                >
                  Phone number
                </Typography>
                <TextField
                  fullWidth
                  value={data?.phoneNumber}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "");
                    setData({ ...data, phoneNumber: onlyDigits });
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  InputProps={{
                    readOnly: !editing,
                    startAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Select
                          variant="standard"
                          value={data?.phoneCode}
                          onChange={handleCountryCodeChange}
                          disableUnderline
                          disabled={!editing}
                          sx={{ ...selectInAdornmentSx, minWidth: 65 }}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                              {c.code}
                            </MenuItem>
                          ))}
                        </Select>
                        <Divider
                          variant="middle"
                          orientation="vertical"
                          flexItem
                          sx={{ borderColor: "#111", pl: 1 }}
                        />
                      </Box>
                    ),
                  }}
                  placeholder={editing ? "088 888 8888" : ""}
                  sx={
                    editing
                      ? fieldSx
                      : {
                          "& fieldset": {
                            border: "none",
                          },
                          backgroundColor: "#f7f7f7",
                          borderRadius: "10px",
                          "& input": {
                            padding: "12px 14px",
                          },
                        }
                  }
                />
              </FormControl>
            </Grid>
          </Box>
        </Grid>
      )}

      {/* Save Button - Only show when editing */}
      {editing && !loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            onClick={handleSave}
            disabled={savingState}
            sx={{
              width: 220,
              height: 52,
              borderRadius: "14px",
              backgroundColor: "#111",
              textTransform: "none",
              fontSize: 16,
              fontWeight: 500,
              "&:hover": { backgroundColor: "#111" },
              "&:disabled": { backgroundColor: "#ccc", color: "#666" },
            }}
            variant="contained"
          >
            {savingState ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default PersonalInfo;
